import React, { useRef, useState } from 'react'
import { styles } from '../../../../../../styles/tailwind-config'
import { Label } from '../../../../../../components/ui/label'
import { Input } from '../../../../../../components/ui/input'
import { Button } from '../../../../../../components/ui/button'
import { CameraIcon, CheckIcon, PlusIcon, Trash2Icon } from 'lucide-react'
import { PHOTO, PHOTO_SOORT, useInterventionInput } from '../../../../../../context/InterventionInputContext'
import { z } from 'zod'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../../../../components/ui/dropdown-menu'
import { format } from 'date-fns'
import { getCustomSettings } from '../../../../../../config/custom-settings'
import { useAuth } from '../../../../../../context/AuthProvider';
import { Trans, useLingui } from '@lingui/react/macro'

const OptionalData = () => {
  const { auth } = useAuth()
  const { t } = useLingui()
  const { intervention, email_addresses, setemail_addresses, photos, setphotos, photosHydrated } = useInterventionInput()
  const [email, setemail] = useState('');
  const fileId = React.useId();

  const [selected_images, setselected_images] = useState<number[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const deferredPhotos = React.useDeferredValue(photos);
  const visiblePhotos = React.useMemo(() => {
    const list = (deferredPhotos ?? []).map((p, idx) => ({ photo: p, idx }));
    return list.filter(({ photo }) =>
      photo.soort === 'E_FOTO' || photo.soort === 'I_FOTO' || photo.soort === 'FOTO'
    );
  }, [deferredPhotos]);


  const defaultFileType = getCustomSettings(auth.data?.databases[0].dbid)?.intervention?.optional_data?.type_photos

  const fileType = useRef<PHOTO_SOORT>(defaultFileType ? defaultFileType[0] : "I_FOTO");

  function isValidEmail(email: string) {
    try {
      z.object({
        email: z.string().email()
      }).parse({
        email: email
      })
      return true
    } catch (e) {
      return false
    }
  }

  function handleAddEmail() {
    if (!isValidEmail(email)) return;
    setemail_addresses(prev => ([...(prev ?? []), email]));
    setemail('')
  }

  function handleRemoveEmail(email: string) {
    email_addresses && setemail_addresses(prev => (prev ?? []).filter(e => e !== email))
  }

  function handleToggleImage(originalIdx: number) {
    setselected_images(prev =>
      prev.includes(originalIdx) ? prev.filter(i => i !== originalIdx) : [...prev, originalIdx]
    );
  }

  function handleRemoveImages() {
    setphotos(prev => (prev ?? []).filter((_, idx) => !selected_images.includes(idx)));
    setselected_images([]);
  }

  async function fileToImage(file: File): Promise<HTMLImageElement> {
    const url = URL.createObjectURL(file);
    try {
      return await new Promise((res, rej) => {
        const img = new Image();
        img.onload = () => res(img);
        img.onerror = rej;
        img.src = url;
      });
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  function drawScaled(img: HTMLImageElement, maxSize: number) {
    const { width, height } = img;
    const scale = Math.min(1, maxSize / Math.max(width, height));
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
    return canvas;
  }

  async function compressToDataUrl(file: File, maxSize: number, quality: number) {
    const img = await fileToImage(file);
    const canvas = drawScaled(img, maxSize);
    return canvas.toDataURL('image/jpeg', quality);
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;

    const soort: PHOTO_SOORT = fileType.current || 'I_FOTO';

    const readAsDataURL = (file: File) =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

    (async () => {
      const now = new Date();
      const added: PHOTO[] = [];

      for (const file of files) {
        try {
          const b64 = await compressToDataUrl(file, 1600, 0.8);
          if (!b64) continue;
          added.push({
            interventionid: String(intervention?.m_iINTERVENTIESID ?? ''),
            photo: b64,
            soort,
            attestid: '',
            customerdocid: '',
            docid: '',
            machineid: '',
            otherusedpartid: '',
            timestamp: format(now, 'yyyyMMddHHmmss'),
            usedpartid: '',
          });
        } catch (err) {
          // Skip the single failing file; don't abort the batch
          console.warn('Skipping a photo that failed to read:', err);
        }
      }

      if (added.length > 0) {
        setphotos(prev => ([...(prev ?? []), ...added]));
      } else {
        console.warn('All selected photos failed to load.');
      }

      if (fileInputRef.current) fileInputRef.current.value = '';
    })();
  }

  if (!photosHydrated) {
    return (
      <div className="p-4 text-sm text-neutral-500">
        Gegevens worden geladen…
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      <h3 className={`${styles.h3}`}><Trans>Optionele gegevens</Trans></h3>

      <div>
        <Label><Trans>Extra e-mailadressen</Trans></Label>
        <div className='flex items-center gap-2'>
          <Input value={email} onChange={(e) => { setemail(e.currentTarget.value) }} className='w-full' placeholder={t`Type een e-mailadres...`} type='email' />
          <Button onClick={() => { handleAddEmail() }} disabled={!isValidEmail(email)} className='p-0 aspect-square'><PlusIcon className='w-4 h-4' /></Button>
        </div>
      </div>

      <div className='border rounded-md'>
        {email_addresses && email_addresses.map((email, i) => (
          <div className='flex items-center justify-between border-b last:border-b-0' key={i}>
            <p className='py-3 pl-2 text-sm font-medium text-neutral-700'>{email}</p>
            <button onClick={() => { handleRemoveEmail(email) }} className='flex items-center justify-center w-8 h-8 text-red-600'><Trash2Icon className='w-4 h-4' /></button>
          </div>
        ))}
      </div>

      <h3 className={`${styles.h3}`}><Trans>Foto's toevoegen</Trans></h3>

      <div className='flex items-center justify-between h-10 p-1 border rounded-full'>
        <input
          ref={fileInputRef}
          id={fileId}
          type='file'
          accept='image/*,image/heic,image/heif'
          className='sr-only'
          onChange={handleFileChange}
          multiple
          onClick={e => { (e.currentTarget as HTMLInputElement).value = '' }}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className='flex items-center justify-center w-8 h-full text-blue-600 rounded-full aspect-square'>
              <CameraIcon className='w-4 h-4' />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent>
            {(getCustomSettings(auth.data?.databases[0].dbid)
              ?.intervention?.optional_data?.type_photos ?? ['I_FOTO']
            ).map((type) => (
              <DropdownMenuItem key={type} asChild>
                <label
                  htmlFor={fileId}
                  className='w-full cursor-pointer'
                  onPointerDown={() => {
                    fileType.current = type as PHOTO_SOORT;
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                >
                  {type === 'E_FOTO' ? t`Externe foto`
                    : type === 'I_FOTO' ? t`Interne foto`
                      : t`Foto`}
                </label>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          onClick={handleRemoveImages}
          disabled={selected_images.length === 0}
          className='flex items-center justify-center w-8 h-full text-red-600 rounded-full aspect-square disabled:opacity-30'
        >
          <Trash2Icon className='w-4 h-4' />
        </button>
      </div>

      <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
        {visiblePhotos.map(({ photo, idx }) => (
          <button
            key={`photo-${idx}`}
            onClick={() => handleToggleImage(idx)}
            className='relative w-full overflow-hidden rounded-md min-h-32 aspect-video bg-neutral-100'
            style={{ contentVisibility: 'auto', containIntrinsicSize: '160px 90px' }}
          >
            {selected_images.includes(idx) && (
              <div className='absolute right-2 top-2 flex items-center justify-center w-6 h-6 text-white bg-blue-600 border-2 border-white rounded-full'>
                <CheckIcon className='w-4 h-4' />
              </div>
            )}
            <p className='absolute px-1 text-xs font-medium bg-white rounded-full left-2 bottom-2 text-primary'>
              {photo.soort === 'E_FOTO' ? t`Extern` : (photo.soort === 'I_FOTO' ? t`Intern` : t`Foto`)}
            </p>
            <img
              src={photo.photo || ''}
              className='object-cover w-full h-full'
              alt='MountQ image'
              loading='lazy'
              decoding='async'
            />
          </button>
        ))}
      </div>

    </div>
  )
}

export default OptionalData